import { useEffect } from "react";
import { JSX } from "react";
import { useNavigate } from "react-router";
import useAuth from "../../../shared/hooks/use-auth";
import { LoaderIcon } from "lucide-react";

interface ProtectedRouteProps {
  children: JSX.Element;
  redirectTo: string;
}

const ProtectedRoute = ({ children, redirectTo }: ProtectedRouteProps) => {
  const {
    isAuthenticated,
    user,
    isLoading,
    canAccessApplication,
    isStaffUser,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      // Check if user can access the application (has system role or admin/owner org role)
      if (!canAccessApplication) {
        // User is authenticated but doesn't have required access level
        // Check if they have staff-level access in any organization
        if (isStaffUser) {
          // Staff-level user - redirect to staff access page
          navigate("/staff-access", { replace: true });
        } else {
          // No valid role - redirect to unauthorized
          navigate("/401", { replace: true });
        }
        return;
      }
    } else if (!isLoading && !isAuthenticated) {
      // User is not authenticated at all
      navigate(redirectTo, { replace: true });
    }
  }, [
    isLoading,
    isAuthenticated,
    user,
    redirectTo,
    navigate,
    canAccessApplication,
    isStaffUser,
  ]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-screen">
          <LoaderIcon
            height={40}
            width={40}
            className="animate-spin text-muted-foreground"
          />
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
